import { isEqual } from "lodash";

import AppLink from "@components/link/link";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconType } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";

const tabClassName =
  "text-gray-400 font-medium py-2 text-sm border-b-2 border-transparent shrink-0 flex-nowrap";

type TabOption<T> = {
  value: T;
  label: string;
  to?: string;
  hasError?: boolean;
  /** Shows the step number in a circle before the label, for tabs worked
   * through in order. */
  stepNumber?: number;
  /** Drawn in the step circle in place of a number, for a tab that ends
   * the steps rather than being one of them. */
  stepIcon?: TopicflowIconType;
  /** Turns the step circle green, unless the tab is selected. The circle
   * only shows with a `stepNumber` or `stepIcon`; without one, this only
   * turns the label green. */
  isComplete?: boolean;
  disabled?: boolean;
  tooltip?: string;
};

/** What a screen reader says for a tab button. The label alone would hide
 * the step number and the markers, since they are only drawn. */
const accessibleName = <T,>({
  label,
  stepNumber,
  isComplete,
  hasError,
}: TabOption<T>) =>
  [
    label,
    stepNumber !== undefined && `step ${stepNumber}`,
    isComplete && "completed",
    hasError && "has errors",
  ]
    .filter(Boolean)
    .join(", ");

const Tabs = <T,>({
  options,
  selectedValue,
  onClickOptionValue,
  className = "",
}: {
  options: TabOption<T>[];
  selectedValue: T;
  onClickOptionValue?: (value: T) => void;
  className?: string;
}) => {
  const renderStepCircle = (
    { stepNumber, stepIcon, isComplete, disabled }: TabOption<T>,
    isSelected: boolean,
  ) => (
    <span
      className={classNames(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        isSelected
          ? "bg-blue-link text-white"
          : isComplete
            ? "bg-emerald-500 text-white"
            : disabled
              ? "bg-gray-100 text-gray-300"
              : "bg-gray-200 text-gray-500",
      )}
    >
      {stepIcon ? (
        <TopicflowIcon type={stepIcon} className="h-3 w-3" />
      ) : (
        stepNumber
      )}
    </span>
  );
  const renderLabel = (option: TabOption<T>, isSelected: boolean) => (
    <span className="inline-flex items-center gap-1.5">
      {(option.stepNumber !== undefined || option.stepIcon !== undefined) &&
        renderStepCircle(option, isSelected)}
      {option.label}
      {option.hasError && (
        <span
          aria-label={`${option.label} has errors`}
          className="inline-block h-1.5 w-1.5 rounded-full bg-red-500"
        />
      )}
    </span>
  );
  return (
    <div
      className={classNames(
        "flex items-center gap-8 border-b overflow-x-auto flex-nowrap",
        className,
      )}
    >
      {options.map((option) => {
        const { value, label, to, isComplete, disabled, tooltip } = option;
        const isSelected = isEqual(value, selectedValue);
        if (to) {
          return (
            <AppLink
              key={label}
              className={classNames(
                tabClassName,
                isSelected
                  ? "border-blue-link text-blue-link hover:text-blue-link"
                  : " hover:text-gray-700 hover:border-gray-200",
              )}
              to={to}
              onClick={() => onClickOptionValue?.(value)}
            >
              {renderLabel(option, isSelected)}
            </AppLink>
          );
        }
        const button = (
          <button
            key={label}
            className={classNames(
              tabClassName,
              isSelected
                ? "border-blue-link text-blue-link hover:text-blue-link"
                : disabled
                  ? "text-gray-300 cursor-default"
                  : isComplete
                    ? "text-emerald-600 hover:text-emerald-700 hover:border-gray-200"
                    : " hover:text-gray-700 hover:border-gray-200",
            )}
            aria-label={accessibleName(option)}
            disabled={disabled}
            onClick={() => onClickOptionValue?.(value)}
          >
            {renderLabel(option, isSelected)}
          </button>
        );
        if (!tooltip) {
          return button;
        }
        // A disabled button gets no hover, so the tooltip hangs off a wrapper.
        return (
          <Tooltip key={label} text={tooltip}>
            <span className="flex shrink-0">{button}</span>
          </Tooltip>
        );
      })}
      <div className={classNames(tabClassName, "flex-1")}>&nbsp;</div>
    </div>
  );
};

export default Tabs;
